/**
 * Card Payment Component
 * Stripe integration for card payments
 */

import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { paymentService } from "../services/api/payment.service";
import type { CertificateApplication } from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import "../styles/pages/Users.css";

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe(
  "pk_test_51QqvMhRuIAZ9rptGGaJpNEfGQnLGJ8GRjTGrWmPv5sXuM8XT8uEQEiHNdJP7PVUqvYh9KOxW6iP9MYRzTLPeHzLY00CFn0qXnN",
);

interface CardPaymentProps {
  application: CertificateApplication;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

const CardPaymentForm: React.FC<CardPaymentProps> = ({
  application,
  onClose,
  onPaymentSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!stripe || !elements) {
      setFormError("Stripe is not properly initialized");
      return;
    }

    // Show confirmation dialog
    const Swal = (await import("sweetalert2")).default;
    const confirmed = await Swal.fire({
      title: "Confirm Payment",
      text: "Are you sure you want to process this payment? Please ensure all card details are correct.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#17a2b8",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, process payment",
      cancelButtonText: "Cancel",
    });
    if (!confirmed.isConfirmed) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setFormError("Card element not found");
      return;
    }

    // Validation
    if (!application.id || application.id <= 0) {
      setFormError("Invalid certificate application");
      return;
    }
    const lraPayment = application.lraPayment ?? application.serviceCharge ?? 0;
    const operationalCost = application.operationalCost ?? 0;

    if (lraPayment <= 0) {
      setFormError("Invalid payment amount");
      return;
    }

    try {
      setFormLoading(true);
      console.log("=== CARD PAYMENT SUBMIT ===");
      console.log("Application:", application);

      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        });

      if (stripeError) {
        throw new Error(stripeError.message || "Failed to process card");
      }

      console.log("Stripe Payment Method created:", paymentMethod);

      // Create payment record in backend
      const paymentData = {
        certificateApplicationId: application.id,
        lraPayment,
        operationalCost,
        currencyId: application.currencyId || 1,
        statusId: UserStatusId.Created,
        paymentMode: "Card",
      };

      console.log("Creating payment record:", paymentData);
      await paymentService.createPayment(paymentData);

      console.log("Payment created successfully!");
      showSuccessToast("Payment processed successfully");
      onPaymentSuccess?.();
      onClose();
    } catch (err) {
      console.error("Payment failed:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--side-panel">
      <div className="modal-content modal-content--side-panel">
        <BusyOverlay isVisible={formLoading} message="Processing payment..." />

        <div className="modal-header">
          <h2 className="modal-title">Card Payment</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={formLoading}
          >
            ✕
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmitPayment}>
          {formError && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{formError}</span>
              <button
                type="button"
                onClick={() => setFormError("")}
                className="alert-close"
              >
                ✕
              </button>
            </div>
          )}

          {/* Application Information Display */}
          <div
            style={{
              background: "#f8f9fa",
              padding: "1rem",
              borderRadius: "4px",
              marginBottom: "1.5rem",
              border: "1px solid #dee2e6",
            }}
          >
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: "600",
                marginBottom: "0.75rem",
                color: "#495057",
              }}
            >
              Application Details
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.5rem",
                fontSize: "0.875rem",
              }}
            >
              <div>
                <span style={{ color: "#6c757d" }}>Form No:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.formNo}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Category:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.category}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>County:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.countyName || "—"}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Email:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.email || "—"}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Amount to Pay:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.currencyName || ""}{" "}
                  {application.serviceCharge || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Stripe Card Element */}
          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
            >
              Card Details <span className="required">*</span>
            </label>
            <div
              style={{
                padding: "0.75rem",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                background: "#fff",
              }}
            >
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </div>
          </div>

          <div
            className="alert"
            style={{
              background: "#d1ecf1",
              border: "1px solid #bee5eb",
              borderRadius: "4px",
              padding: "0.75rem",
              marginBottom: "1.5rem",
              fontSize: "0.875rem",
            }}
          >
            <strong>💳 Secure Payment:</strong> Your card details are encrypted
            and processed securely by Stripe. We never store your card
            information.
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={formLoading}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={formLoading || !stripe}
            >
              {formLoading ? (
                <>
                  <span className="spinner spinner--sm"></span>
                  Processing...
                </>
              ) : (
                `Pay ${application.currencyName || ""} ${application.serviceCharge || 0}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const CardPayment: React.FC<CardPaymentProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <CardPaymentForm {...props} />
    </Elements>
  );
};

export default CardPayment;
